import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FcOpenTradesTable, FcClosedTradesTable } from './FcPaperTradeTables';
import { fmtSignalTime } from '../utils/fmtPrice';
import './AdminMultiAssetBotSettings.css';
import './FundingCarryTrading.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

export default function AdminFundingCarrySettings() {
  const [global, setGlobal] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveMsg, setSaveMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  const [scanIntervalSec, setScanIntervalSec] = useState('3600');
  const [minOveScore, setMinOveScore] = useState('49');
  const [takeProfitPct, setTakeProfitPct] = useState('0.8');
  const [squeezeMaxRange, setSqueezeMaxRange] = useState('1.8');
  const [exitOnTrendBreak, setExitOnTrendBreak] = useState(false);
  const [trendEmaHours, setTrendEmaHours] = useState('48');
  const [atrStop, setAtrStop] = useState('2.5');
  const [defaultNotional, setDefaultNotional] = useState('2500');
  const [maxLeverage, setMaxLeverage] = useState('3');
  const [maxPositions, setMaxPositions] = useState('4');
  const [universeJson, setUniverseJson] = useState('[]');

  const [commissionEnabled, setCommissionEnabled] = useState(false);
  const [commissionWallet, setCommissionWallet] = useState('');
  const [commissionBpsBuy, setCommissionBpsBuy] = useState('4');
  const [commissionBpsSell, setCommissionBpsSell] = useState('4');
  const [commissionMinUsd, setCommissionMinUsd] = useState('0.05');
  const [commissionBlockWhenOwed, setCommissionBlockWhenOwed] = useState(true);
  const [commissionOwedBlockUsd, setCommissionOwedBlockUsd] = useState('1');
  const [backtestDays, setBacktestDays] = useState('90');
  const [backtestRunning, setBacktestRunning] = useState(false);
  const [backtestResult, setBacktestResult] = useState(null);
  const [paperSummary, setPaperSummary] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: gData }, { data: dData }] = await Promise.all([
        axios.get(`${API_BASE}/funding-carry/admin/global`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/funding-carry/admin/detail`, { headers: authHeaders() }),
      ]);
      const g = gData.global;
      setGlobal(g);
      setDetail(dData);
      setScanIntervalSec(String(Math.round((g.scanIntervalMs || 3600000) / 1000)));
      setMinOveScore(String(g.rules?.minOveScore ?? 49));
      setTakeProfitPct(String(((g.rules?.takeProfitPct ?? 0.008) * 100).toFixed(2)));
      setSqueezeMaxRange(String(g.rules?.squeezeMaxRangeAtrRatio ?? 1.8));
      setExitOnTrendBreak(!!g.rules?.exitOnTrendBreak);
      setTrendEmaHours(String(g.rules?.trendEmaHours ?? 48));
      setAtrStop(String(g.rules?.atrStopMultiplier ?? 2.5));
      setDefaultNotional(String(g.risk?.defaultNotionalUsd ?? 2500));
      setMaxLeverage(String(g.risk?.maxLeverage ?? 3));
      setMaxPositions(String(g.risk?.maxConcurrentPositions ?? 4));
      setUniverseJson(JSON.stringify(g.universe || [], null, 2));
      const c = g.commission || {};
      setCommissionEnabled(!!c.enabled);
      setCommissionWallet(c.walletAddress || '');
      setCommissionBpsBuy(String(c.bpsBuy ?? 4));
      setCommissionBpsSell(String(c.bpsSell ?? 4));
      setCommissionMinUsd(String(c.minUsd ?? 0.05));
      setCommissionBlockWhenOwed(!!c.blockWhenOwed);
      setCommissionOwedBlockUsd(String(c.owedBlockUsd ?? 1));
      setError(null);

      try {
        const { data: pData } = await axios.get(`${API_BASE}/funding-carry/admin/paper-summary`, {
          headers: authHeaders(),
        });
        setPaperSummary(pData);
      } catch (paperErr) {
        setPaperSummary(null);
        console.warn('[oveBot] paper-summary unavailable:', paperErr.response?.data?.error || paperErr.message);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const patchGlobal = async (body, note) => {
    setSaving(true);
    setSaveMsg(null);
    setError(null);
    try {
      const { data } = await axios.patch(`${API_BASE}/funding-carry/admin/global`, body, {
        headers: authHeaders(),
      });
      setGlobal(data.global);
      setSaveMsg(note || 'Saved');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const savePlatform = async (e) => {
    e.preventDefault();
    const sec = parseInt(scanIntervalSec, 10);
    if (!Number.isFinite(sec) || sec < 60 || sec > 86400) {
      setError('Scan interval must be between 60 and 86400 seconds');
      return;
    }
    let universe;
    try {
      universe = JSON.parse(universeJson);
      if (!Array.isArray(universe)) throw new Error('Universe must be a JSON array');
    } catch (err) {
      setError(`Universe JSON: ${err.message}`);
      return;
    }
    await patchGlobal(
      {
        scanIntervalMs: sec * 1000,
        rulesPatch: {
          engine: 'ove',
          minOveScore: parseInt(minOveScore, 10),
          takeProfitPct: parseFloat(takeProfitPct) / 100,
          squeezeMaxRangeAtrRatio: parseFloat(squeezeMaxRange),
          trendEmaHours: parseInt(trendEmaHours, 10),
          atrStopMultiplier: parseFloat(atrStop),
          exitOnTrendBreak,
        },
        riskPatch: {
          defaultNotionalUsd: parseFloat(defaultNotional),
          maxLeverage: Math.min(parseFloat(maxLeverage) || 3, global?.absoluteMaxLeverage || 5),
          maxConcurrentPositions: parseInt(maxPositions, 10),
        },
        universe,
        commission: {
          enabled: commissionEnabled,
          wallet: commissionWallet.trim(),
          bpsBuy: parseFloat(commissionBpsBuy) || 0,
          bpsSell: parseFloat(commissionBpsSell) || 0,
          minUsd: parseFloat(commissionMinUsd) || 0.05,
          blockWhenOwed: commissionBlockWhenOwed,
          owedBlockUsd: parseFloat(commissionOwedBlockUsd) || 1,
        },
      },
      'Platform settings saved'
    );
  };

  const checkInbox = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/funding-carry/admin/inbox/check-now`, {}, { headers: authHeaders() });
      await load();
      setSaveMsg('Inbox checked');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const scanNow = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/funding-carry/admin/scan-now`, {}, { headers: authHeaders() });
      await load();
      setSaveMsg('Scan complete');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const runBacktest = async () => {
    setBacktestRunning(true);
    setError(null);
    try {
      const days = Math.min(365, Math.max(30, parseInt(backtestDays, 10) || 90));
      const { data } = await axios.post(
        `${API_BASE}/funding-carry/admin/backtest`,
        { days },
        { headers: authHeaders() }
      );
      setBacktestResult(data.result);
      setSaveMsg(`Backtest complete (${days} days).`);
      if (data.result?.universeRepaired) {
        await load();
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBacktestRunning(false);
    }
  };

  const repairRules = async () => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/funding-carry/admin/repair-rules`,
        {},
        { headers: authHeaders() }
      );
      await load();
      setSaveMsg(
        data.repaired || data.universeRepair?.repaired
          ? `Repaired${data.universeRepair?.repaired ? ' universe (removed HYPE/TSLA/NATGAS)' : ''}${data.repaired ? ' rules' : ''}.`
          : 'OVE rules and universe already valid.'
      );
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (presetId) => patchGlobal({ riskPreset: presetId }, `Applied ${presetId} preset`);

  const applyUniversePreset = (presetId) =>
    patchGlobal({ universePreset: presetId }, `Applied ${presetId} universe`);

  if (loading) return <p>Loading oveBot settings…</p>;
  if (!global) return <p>No config found.</p>;

  return (
    <div className="admin-multi-asset-settings">
      <h2>⚡ oveBot (Omni Volatility Expansion)</h2>
      <p className="admin-hint">
        24/7 OVE engine scans HL perps for vol compression → breakout setups. Traders see subscription, book, and YTD
        (live only). Admin sees paper YTD + signals.
      </p>

      {error && <div className="error-message">{error}</div>}
      {saveMsg && <div className="success-message">{saveMsg}</div>}

      <div className="admin-form-group">
        <label>
          <input
            type="checkbox"
            checked={!!global.enabled}
            disabled={saving}
            onChange={(e) => patchGlobal({ enabled: e.target.checked }, e.target.checked ? 'Engine ON' : 'Engine OFF')}
          />
          Engine enabled (24/7 worker)
        </label>
      </div>

      <div className="admin-form-group">
        <label>
          <input
            type="checkbox"
            checked={!!global.paperMode}
            disabled={saving}
            onChange={(e) => patchGlobal({ paperMode: e.target.checked })}
          />
          Paper mode (global)
        </label>
      </div>

      <section className="fc-admin-paper">
        <h3>Paper trading (live forward test)</h3>
        <p className="admin-hint">
          Backtest = historical replay. <strong>Paper</strong> = engine runs 24/7 on live HL prices and simulates
          fills in the database (no real orders). Compare paper results to backtest over 4–8 weeks before live.
        </p>
        <ol className="fc-paper-checklist">
          <li className={global.enabled ? 'fc-check-ok' : 'fc-check-warn'}>
            Engine enabled {global.enabled ? '✓' : '— turn on above'}
          </li>
          <li className={global.paperMode ? 'fc-check-ok' : 'fc-check-warn'}>
            Paper mode ON {global.paperMode ? '✓' : '— required for simulated fills'}
          </li>
          <li>
            Worker running: <code>pm2 status omni-funding-carry-worker</code>
            {paperSummary?.lastScanAt && (
              <>
                {' '}
                · last scan {new Date(paperSummary.lastScanAt).toLocaleString()}
              </>
            )}
          </li>
          <li>
            Subscribe your account: open <a href="/ovebot">/ovebot</a> → Enable paper oveBot
          </li>
        </ol>
        {paperSummary?.lastError && (
          <p className="error-message">Worker error: {paperSummary.lastError}</p>
        )}
        {paperSummary?.summary && (
          <>
            {paperSummary.summary.pacing && (
              <div className={`fc-paper-pace fc-paper-pace-${paperSummary.summary.pacing.status}`}>
                <strong>6mo backtest pace (expanded universe, 15 bps)</strong>
                <p>
                  Target:{' '}
                  <strong>
                    {paperSummary.summary.pacing.targetTripsPerMonthLow}–
                    {paperSummary.summary.pacing.targetTripsPerMonthHigh} closed trips / month
                  </strong>{' '}
                  (~{paperSummary.summary.pacing.backtestTrips} trips /{' '}
                  {paperSummary.summary.pacing.backtestDays}d reference).
                </p>
                {paperSummary.summary.pacing.tripsPerMonth != null ? (
                  <p>
                    Current pace:{' '}
                    <strong>{paperSummary.summary.pacing.tripsPerMonth} trips / month</strong>
                    {paperSummary.summary.pacing.paperAgeDays != null && (
                      <>
                        {' '}
                        ({paperSummary.summary.pacing.closedTrips} closed in{' '}
                        {Math.round(paperSummary.summary.pacing.paperAgeDays)}d)
                      </>
                    )}
                    {paperSummary.summary.pacing.projectedTrips6Mo != null && (
                      <>
                        {' '}
                        · projected ~{paperSummary.summary.pacing.projectedTrips6Mo} trips / 6mo
                      </>
                    )}
                  </p>
                ) : (
                  <p>
                    Current: {paperSummary.summary.pacing.closedTrips} closed trip
                    {paperSummary.summary.pacing.closedTrips === 1 ? '' : 's'}
                    {paperSummary.summary.pacing.paperAgeDays != null && (
                      <> · paper running {Math.round(paperSummary.summary.pacing.paperAgeDays)}d</>
                    )}
                  </p>
                )}
                <p className="fc-paper-pace-status">{paperSummary.summary.pacing.statusLabel}</p>
              </div>
            )}
            {paperSummary.summary.epoch?.baseline && (
              <div className="fc-paper-epoch">
                <strong>v4 measurement (compare here — not YTD)</strong>
                <p className="admin-hint">
                  YTD / all-time still include the pre-v4 hole. Use this section to judge whether v4
                  rules are working.
                </p>
                <p>
                  <strong>Pre-v4 baseline (frozen):</strong>{' '}
                  {paperSummary.summary.epoch.baseline.closedTrips} trips · trader net{' '}
                  <span
                    className={
                      (paperSummary.summary.epoch.baseline.traderNetUsd || 0) >= 0 ? 'fc-pos' : 'fc-neg'
                    }
                  >
                    ${Number(paperSummary.summary.epoch.baseline.traderNetUsd || 0).toFixed(2)}
                  </span>
                  {paperSummary.summary.epoch.epochAt && (
                    <>
                      {' '}
                      · recorded {fmtSignalTime(paperSummary.summary.epoch.epochAt)}
                    </>
                  )}
                </p>
                {paperSummary.summary.epoch.since && (
                  <>
                    <p>
                      <strong>Since v4:</strong> {paperSummary.summary.epoch.since.closedTrips} closed
                      trips · trader net{' '}
                      <span
                        className={
                          (paperSummary.summary.epoch.since.traderNetUsd || 0) >= 0 ? 'fc-pos' : 'fc-neg'
                        }
                      >
                        ${Number(paperSummary.summary.epoch.since.traderNetUsd || 0).toFixed(2)}
                      </span>
                      {paperSummary.summary.epoch.since.pacing?.tripsPerMonth != null && (
                        <>
                          {' '}
                          · {paperSummary.summary.epoch.since.pacing.tripsPerMonth} trips / month
                        </>
                      )}
                    </p>
                    {paperSummary.summary.epoch.since.pacing && (
                      <p
                        className={`fc-paper-pace-status fc-paper-pace-${paperSummary.summary.epoch.since.pacing.status}`}
                      >
                        {paperSummary.summary.epoch.since.pacing.statusLabel}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
            {!paperSummary.summary.epoch?.baseline && (
              <p className="admin-hint fc-paper-epoch-hint">
                After you deploy v4, run <strong>Repair rules</strong> or a backtest once — admin will
                freeze today&apos;s stats as the pre-v4 baseline so you don&apos;t have to remember
                −18.29%.
              </p>
            )}
            <p>
              <strong>Platform paper (all subscribers):</strong>{' '}
              {paperSummary.summary.subscriberCount} subscribed · {paperSummary.summary.totalOpenPositions} open ·{' '}
              {paperSummary.summary.allTime?.roundTrips ?? 0} closed trips · win{' '}
              {paperSummary.summary.winRatePct ?? 0}%
            </p>
            <p>
              <strong>Trader net (all time):</strong>{' '}
              <span
                className={
                  (paperSummary.summary.allTime?.traderNetUsd ?? 0) >= 0 ? 'fc-pos' : 'fc-neg'
                }
              >
                ${Number(paperSummary.summary.allTime?.traderNetUsd ?? 0).toFixed(2)}
              </span>
              {' · '}
              <strong>Omni net:</strong>{' '}
              <span className="fc-pos">
                ${Number(paperSummary.summary.allTime?.omniNetUsd ?? 0).toFixed(2)}
              </span>
              {' · '}
              <strong>YTD:</strong>{' '}
              <span
                className={(paperSummary.summary.ytd?.netPnlUsd ?? 0) >= 0 ? 'fc-pos' : 'fc-neg'}
              >
                ${Number(paperSummary.summary.ytd?.netPnlUsd ?? 0).toFixed(2)}
              </span>
            </p>
            {Object.keys(paperSummary.summary.bySymbol || {}).length > 0 && (
              <table className="fc-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Trips</th>
                    <th>Trader net $</th>
                    <th>Omni net $</th>
                    <th>W</th>
                    <th>L</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(paperSummary.summary.bySymbol).map(([sym, row]) => (
                    <tr key={sym}>
                      <td>{sym}</td>
                      <td>{row.roundTrips ?? 0}</td>
                      <td className={(row.traderNetUsd || 0) >= 0 ? 'fc-pos' : 'fc-neg'}>
                        ${Number(row.traderNetUsd || 0).toFixed(2)}
                      </td>
                      <td className="fc-pos">${Number(row.omniNetUsd || 0).toFixed(2)}</td>
                      <td>{row.wins ?? 0}</td>
                      <td>{row.losses ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(paperSummary.openPositions || []).length > 0 && (
              <>
                <h4>Open paper positions (platform)</h4>
                <FcOpenTradesTable trades={paperSummary.openPositions} showUser />
              </>
            )}
            {(paperSummary.recentClosed || []).length > 0 && (
              <>
                <h4>Recent closed paper trades (platform)</h4>
                <FcClosedTradesTable trades={paperSummary.recentClosed.slice(0, 12)} showUser />
              </>
            )}
          </>
        )}
        <button type="button" className="admin-btn" disabled={saving} onClick={scanNow}>
          Run scan now (paper tick)
        </button>
      </section>

      <section className="fc-admin-presets">
        <h3>Risk tier preset</h3>
        <p className="admin-hint">
          Platform-wide cap for all oveBots. Absolute max leverage is{' '}
          <strong>{global.absoluteMaxLeverage ?? 5}x</strong>. Manual sizing below marks the tier as{' '}
          <strong>Custom</strong>.
        </p>
        <p className="admin-hint">
          Active tier: <strong>{global.riskTier?.label || global.riskPreset || 'standard'}</strong>
          {global.riskPreset && global.riskPreset !== 'custom' ? ` (${global.riskPreset})` : ''}
        </p>
        <div className="fc-preset-buttons">
          {(global.presets || []).map((p) => (
            <button
              key={p.id}
              type="button"
              className={`admin-btn${global.riskPreset === p.id ? ' primary' : ''}`}
              disabled={saving}
              onClick={() => applyPreset(p.id)}
            >
              {p.label} (max {p.maxLeverage}x)
            </button>
          ))}
        </div>
        <ul className="fc-preset-legend">
          <li>
            <strong>Conservative</strong> — 2x · $5k/position · 3 slots · 6 bps/side
          </li>
          <li>
            <strong>Standard</strong> — 3x · $5k/position · 4 slots · 8 bps/side (default)
          </li>
          <li>
            <strong>Aggressive</strong> — 5x · $5k/position · 4 slots · 10 bps/side
          </li>
        </ul>
      </section>

      <form onSubmit={savePlatform} className="admin-settings-form">
        <h3>Engine timing</h3>
        <div className="admin-form-row">
          <label>Scan interval (seconds)</label>
          <input value={scanIntervalSec} onChange={(e) => setScanIntervalSec(e.target.value)} />
          <span className="field-hint">Min 60s. Worker runs continuously under pm2.</span>
        </div>

        <h3>Strategy rules (admin only — not shown to traders)</h3>
        <p className="admin-hint">
          Active: take-profit {global.rules?.takeProfitPct != null ? `${(global.rules.takeProfitPct * 100).toFixed(2)}%` : '—'}
          {' · '}min OVE score {global.rules?.minOveScore ?? '—'}
          {' · '}trend-break exit {global.rules?.exitOnTrendBreak ? 'on' : 'off'}
        </p>
        <div className="admin-form-row">
          <label>Min OVE score (entry)</label>
          <input value={minOveScore} onChange={(e) => setMinOveScore(e.target.value)} />
        </div>
        <div className="admin-form-row">
          <label>Take-profit (%)</label>
          <input value={takeProfitPct} onChange={(e) => setTakeProfitPct(e.target.value)} />
          <span className="field-hint">e.g. 0.8 = 0.8% move on price</span>
        </div>
        <div className="admin-form-row">
          <label>Squeeze max range / ATR</label>
          <input value={squeezeMaxRange} onChange={(e) => setSqueezeMaxRange(e.target.value)} />
        </div>
        <div className="admin-form-row">
          <label>Trend EMA (hours)</label>
          <input value={trendEmaHours} onChange={(e) => setTrendEmaHours(e.target.value)} />
        </div>
        <div className="admin-form-row">
          <label>ATR stop multiplier</label>
          <input value={atrStop} onChange={(e) => setAtrStop(e.target.value)} />
        </div>
        <div className="admin-form-group">
          <label>
            <input
              type="checkbox"
              checked={exitOnTrendBreak}
              onChange={(e) => setExitOnTrendBreak(e.target.checked)}
            />
            Exit on trend break (EMA cross — usually leave off)
          </label>
        </div>

        <h3>Sizing (applied to all subscriber oBots)</h3>
        <div className="admin-form-row">
          <label>Default notional / position (USD)</label>
          <input value={defaultNotional} onChange={(e) => setDefaultNotional(e.target.value)} />
        </div>
        <div className="admin-form-row">
          <label>Max leverage (admin cap, max {global.absoluteMaxLeverage ?? 5}x)</label>
          <input value={maxLeverage} onChange={(e) => setMaxLeverage(e.target.value)} />
        </div>
        <div className="admin-form-row">
          <label>Max concurrent positions</label>
          <input value={maxPositions} onChange={(e) => setMaxPositions(e.target.value)} />
        </div>

        <h3>Universe</h3>
        <section className="fc-admin-presets">
          <p className="admin-hint">
            Curated HL perps for OVE — not all ~360 markets. Active:{' '}
            <strong>{global.universePreset || 'custom'}</strong> ({global.universe?.length || 0} symbols).
          </p>
          <div className="fc-preset-buttons">
            {(global.universePresets || []).map((p) => (
              <button
                key={p.id}
                type="button"
                className={`admin-btn${global.universePreset === p.id ? ' primary' : ''}`}
                disabled={saving}
                onClick={() => applyUniversePreset(p.id)}
              >
                {p.label} ({p.symbolCount})
              </button>
            ))}
          </div>
          <ul className="fc-preset-legend">
            {(global.universePresets || []).map((p) => (
              <li key={p.id}>
                <strong>{p.label}</strong> — {p.backtestHint}
              </li>
            ))}
            <li>
              <strong>Custom</strong> — edit JSON below (finance review before adding names)
            </li>
          </ul>
        </section>

        <h3>Universe (JSON)</h3>
        <textarea
          className="admin-json-textarea"
          rows={8}
          value={universeJson}
          onChange={(e) => setUniverseJson(e.target.value)}
        />

        <h3>Platform commission (HL BPS)</h3>
        <div className="admin-form-group">
          <label>
            <input
              type="checkbox"
              checked={commissionEnabled}
              onChange={(e) => setCommissionEnabled(e.target.checked)}
            />
            Collect commission on fills
          </label>
        </div>
        <div className="admin-form-row">
          <label>Commission wallet</label>
          <input value={commissionWallet} onChange={(e) => setCommissionWallet(e.target.value)} />
        </div>
        <div className="admin-form-row">
          <label>BPS buy / sell</label>
          <input style={{ width: 70 }} value={commissionBpsBuy} onChange={(e) => setCommissionBpsBuy(e.target.value)} />
          <input style={{ width: 70 }} value={commissionBpsSell} onChange={(e) => setCommissionBpsSell(e.target.value)} />
        </div>
        <div className="admin-form-row">
          <label>Min commission (USD)</label>
          <input value={commissionMinUsd} onChange={(e) => setCommissionMinUsd(e.target.value)} />
        </div>
        <div className="admin-form-group">
          <label>
            <input
              type="checkbox"
              checked={commissionBlockWhenOwed}
              onChange={(e) => setCommissionBlockWhenOwed(e.target.checked)}
            />
            Block trading when commission owed
          </label>
        </div>

        <button type="submit" className="admin-btn primary" disabled={saving}>
          Save platform settings
        </button>
      </form>

      <section className="fc-admin-backtest">
        <h3>Historical backtest (admin)</h3>
        <p className="admin-hint">
          Runs OVE on live Hyperliquid candles with current universe, rules, and platform sizing. Takes ~1–2
          minutes for 90 days (~4 min for 208 days). Request 30–365 days; HL 1h candles only cover the
          last ~208 days (a 365-day request uses all available history, not a full calendar year).
        </p>
        <div className="admin-form-row">
          <label>Days of history</label>
          <input style={{ width: 80 }} value={backtestDays} onChange={(e) => setBacktestDays(e.target.value)} />
          <button type="button" className="admin-btn primary" disabled={backtestRunning || saving} onClick={runBacktest}>
            {backtestRunning ? 'Running backtest…' : 'Run backtest now'}
          </button>
          <button type="button" className="admin-btn" disabled={backtestRunning || saving} onClick={repairRules}>
            Repair rules & universe
          </button>
        </div>
        {backtestResult && backtestResult.engineBuild !== 'ove-rules-v2' && (
          <p className="error-message">
            Old API build — deploy backend engine.js + backtest.js + routes/fundingCarry.js, then{' '}
            <code>pm2 restart omni-api</code>.
          </p>
        )}
        <p className="admin-hint">
          <strong>Trips</strong> = completed round trips (one open + one close per trip), not individual fills.
        </p>
        {backtestResult && (
          <div className="fc-backtest-results">
            <p>
              <strong>Trader net:</strong>{' '}
              <span className={backtestResult.traderNetUsd >= 0 ? 'fc-pos' : 'fc-neg'}>
                ${Number(backtestResult.traderNetUsd ?? backtestResult.netPnlUsd).toFixed(2)}
              </span>
              {' · '}
              <strong>Omni net:</strong>{' '}
              <span className="fc-pos">
                ${Number(backtestResult.omniNetUsd ?? 0).toFixed(2)}
              </span>
              {' · '}
              {backtestResult.roundTrips} trips ({backtestResult.roundTripsPerDay}/day)
              {' · '}
              {backtestResult.wins ?? 0}W / {backtestResult.losses ?? 0}L
              {' · '}
              win {backtestResult.winRatePct}%
            </p>
            <p className="admin-hint">
              ${backtestResult.marginUsd} margin · {backtestResult.leverage}x · {backtestResult.commissionBps} bps ·
              ran {new Date(backtestResult.ranAt).toLocaleString()}
              {backtestResult.engineBuild && <> · build {backtestResult.engineBuild}</>}
              {(backtestResult.rulesUsed || backtestResult.rules) && (
                <>
                  {' · '}TP{' '}
                  {(
                    (backtestResult.rulesUsed?.takeProfitPct ?? backtestResult.rules?.takeProfitPct ?? 0) * 100
                  ).toFixed(2)}
                  %
                  {' · '}OVE≥{backtestResult.rulesUsed?.minOveScore ?? backtestResult.rules?.minOveScore ?? '—'}
                  {' · '}trend-exit{' '}
                  {(backtestResult.rulesUsed?.exitOnTrendBreak ?? backtestResult.rules?.exitOnTrendBreak)
                    ? 'on'
                    : 'off'}
                </>
              )}
            </p>
            {backtestResult.rulesFromDb &&
              (backtestResult.rulesFromDb.takeProfitPct == null ||
                backtestResult.rulesFromDb.takeProfitPct < 0.004) && (
                <p className="error-message">
                  DB had broken take-profit ({String(backtestResult.rulesFromDb.takeProfitPct)}) —{' '}
                  {backtestResult.rulesRepaired ? 'auto-repaired this run' : 'click Repair OVE rules'}.
                </p>
              )}
            {backtestResult.universeRepaired && backtestResult.universeChanges && (
              <p className="admin-hint" style={{ color: '#4ade80' }}>
                Universe updated: removed {backtestResult.universeChanges.removed?.join(', ') || '—'} → added{' '}
                {backtestResult.universeChanges.added?.join(', ') || '—'}. Symbols:{' '}
                {(backtestResult.symbolsUsed || []).join(', ')}
              </p>
            )}
            {backtestResult.historyNote && (
              <p className="admin-hint" style={{ color: '#fbbf24' }}>
                {backtestResult.historyNote}
              </p>
            )}
            {backtestResult.exitReasonCounts && (
              <p className="admin-hint">
                Exits:{' '}
                {Object.entries(backtestResult.exitReasonCounts)
                  .map(([k, n]) => `${k}=${n}`)
                  .join(', ')}
                {backtestResult.exitReasonCounts.take_profit == null && backtestResult.roundTrips > 0 && (
                  <strong style={{ color: '#c44' }}> — no take-profit exits (broken deploy)</strong>
                )}
              </p>
            )}
            <table className="fc-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Trips</th>
                  <th>Trader net $</th>
                  <th>Omni net $</th>
                  <th>W</th>
                  <th>L</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(backtestResult.bySymbol || {}).map(([sym, row]) => (
                  <tr key={sym}>
                    <td>{sym}</td>
                    <td>{row.roundTrips ?? 0}</td>
                    <td
                      className={
                        (row.traderNetUsd ?? row.netPnlUsd ?? 0) >= 0 ? 'fc-pos' : 'fc-neg'
                      }
                    >
                      ${Number(row.traderNetUsd ?? row.netPnlUsd ?? 0).toFixed(2)}
                    </td>
                    <td className="fc-pos">${Number(row.omniNetUsd ?? 0).toFixed(2)}</td>
                    <td>{row.wins ?? 0}</td>
                    <td>{row.losses ?? 0}</td>
                  </tr>
                ))}
                <tr className="fc-table-total">
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td>
                    <strong>{backtestResult.roundTrips}</strong>
                  </td>
                  <td
                    className={
                      (backtestResult.traderNetUsd ?? backtestResult.netPnlUsd) >= 0 ? 'fc-pos' : 'fc-neg'
                    }
                  >
                    <strong>
                      ${Number(backtestResult.traderNetUsd ?? backtestResult.netPnlUsd).toFixed(2)}
                    </strong>
                  </td>
                  <td className="fc-pos">
                    <strong>${Number(backtestResult.omniNetUsd ?? 0).toFixed(2)}</strong>
                  </td>
                  <td>
                    <strong>{backtestResult.wins ?? 0}</strong>
                  </td>
                  <td>
                    <strong>{backtestResult.losses ?? 0}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>

      <h3>JSON inbox (Omni Map style)</h3>
      <div className="admin-form-group">
        <label>
          <input
            type="checkbox"
            checked={!!global.inbox?.enabled}
            disabled={saving}
            onChange={(e) => patchGlobal({ inbox: { enabled: e.target.checked } })}
          />
          Watch funding-carry-inbox/
        </label>
      </div>
      <button type="button" className="admin-btn" disabled={saving} onClick={checkInbox}>
        Check inbox now
      </button>
      <button type="button" className="admin-btn" disabled={saving} onClick={scanNow}>
        Run scan now
      </button>

      {global.lastScanAt && <p className="admin-hint">Last scan: {new Date(global.lastScanAt).toLocaleString()}</p>}
      {global.lastError && <p className="error-message">Worker error: {global.lastError}</p>}

      {detail?.signals?.length > 0 && (
        <>
          <h3>Recent engine signals (admin)</h3>
          <pre className="admin-json-preview">
            {JSON.stringify(detail.signals.slice(0, 8), null, 2)}
          </pre>
        </>
      )}
    </div>
  );
}
